/**
 * What the landing page says, and where each figure comes from.
 *
 * The page is marketing, so the temptation is to write the numbers down as
 * copy. Every number here that the game already knows is **imported from
 * `@shared` instead** — the battlefields are `MAP_SUMMARIES` with their own
 * names and blurbs, a match's side size is `TEAM_SIZE`, a hull's or turret's
 * tier comes off `HULL_PROFILES`/`TURRET_PROFILES`.
 *
 * The reason is the one the game's own docs give for `PASS_THROUGH` being
 * derived from the catalogue rather than restated: a copy goes stale silently,
 * and a landing page is the worst place in the product for that to happen —
 * it is read by people deciding whether to trust the game at all. If someone
 * retunes the deathmatch clock, this page follows. If someone adds a
 * battlefield, this page lists it.
 *
 * The handful of figures that are **not** on the wire (21 hulls, 25 turrets,
 * 105 research nodes, the +20% premium bound) live in the garage catalogue,
 * which is server-side and not importable here. Those are stated as literals
 * and pinned by `landing-content.test.ts` against the shape the page promises,
 * with a comment naming the source. When the garage grows, that test is what
 * fails.
 *
 * Nothing in this file touches the DOM, Vue, or the network. It is data, so
 * the copy can be read in one place and the components can be dumb.
 */
import { TEAM_SIZE } from '@shared/protocol';
import { STOCK_PAINT_ID } from '@shared/liveries';
import { MAP_SUMMARIES } from '@shared/maps';
import {
  ARCHETYPE_LABELS,
  HULL_PROFILES,
  tierForHull,
  tierForTurret,
  TURRET_PROFILES,
  weaponFor,
} from '@shared/weapons';

// ─── The tank in the hero ────────────────────────────────────────────────────

/**
 * The pair on the turntable, and the paint it wears. All three were chosen by
 * photographing candidates, not from their blurbs.
 *
 * **The Lancer, because the hero needs a barrel.** The whole claim is that the
 * gun points somewhere the hull does not, and a gun the eye cannot find makes
 * that claim invisible: the Railspike and the Sunderer are tall boxed housings
 * that photograph as self-propelled guns, and against them the Lancer's long
 * tube reads as a tank from across the room. The Marauder under it is a low
 * conventional chassis with visible running gear — and a tier-3 *credit* item,
 * which matters on a page whose garage section stakes its honesty on premium
 * being bounded. Leading with a diamond-only hull would undercut that in the one
 * image everybody sees. The Falcon photographs a shade better and is exactly
 * that mistake.
 *
 * **The paint is whatever the stock coat is, and that is the whole rule** — the
 * hero must be the tank a visitor actually gets on their first sortie, so the
 * page is never advertising a cosmetic they have not bought.
 *
 * It is Desert Sand now, and the note it replaces is worth keeping because its
 * reasoning was *about the old coat rather than about the rule*: the olive won a
 * comparison against all nine liveries on the grounds that every pattern
 * flattened the authored albedo into mud at this size, while the stock paint was
 * cold gunmetal carrying the artist's own amber turret stripe. Desert Sand is one
 * of those patterns. So the honest position is that this pairing is chosen by the
 * rule and **the resulting image wants an eye check** — if the camouflage does go
 * muddy on the hero at this framing, the lever is the framing or the pair, never
 * putting a paint on the hero that a new account does not own.
 */
export const HERO_TANK = {
  hullId: 'hull_marauder',
  turretId: 'turret_lancer',
  paintId: STOCK_PAINT_ID,
} as const;

/**
 * The liveries the hero may actually wear, one picked fresh per page load.
 *
 * Deliberately not the whole shelf. Every livery in `catalogue.data.ts` that
 * carries a `diamondPrice` is sold **only** for diamonds — the retained
 * `price` on those rows is a valuation and is never charged, exactly as for a
 * premium hull — so wearing one here would be advertising a cosmetic bought
 * with premium currency on the one screen a visitor has not signed in on yet.
 * Precisely three liveries in the whole catalogue carry no `diamondPrice` at
 * all: the issued coat and the two cheapest credit-only schemes. This is that
 * list, literal for the reason `CATALOGUE`'s counts are — that file is
 * server-side and not importable here.
 */
export const HERO_LIVERY_POOL: readonly string[] = [STOCK_PAINT_ID, 'paint_winter', 'paint_woodland'];

// ─── The tank showcase ───────────────────────────────────────────────────────

interface ShowcaseSubject {
  hullId: string;
  turretId: string;
  /** `catalogue.data.ts`'s own `name`, and a literal for the reason `CATALOGUE`'s
   * counts are — that file is server-side and not importable here. */
  hullName: string;
  turretName: string;
}

/**
 * Ten pairs off the shop shelf, picked for a spread of tiers (1 through 5) and
 * gun archetypes rather than curated for looks. Every id is a real catalogue
 * row — `landing-content.test.ts` ships the meshes it promises, the same way
 * it does for `HERO_TANK` — so a renamed or withdrawn hull fails a test
 * instead of quietly breaking a card.
 */
const SHOWCASE_SUBJECTS: readonly ShowcaseSubject[] = [
  { hullId: 'hull_recruit', turretId: 'turret_recruit', hullName: 'Recruit', turretName: 'Recruit 76' },
  { hullId: 'hull_scout', turretId: 'turret_twinstub', hullName: 'Scout', turretName: 'Twin Stub' },
  { hullId: 'hull_dart', turretId: 'turret_buckshot', hullName: 'Dart', turretName: 'Buckshot' },
  { hullId: 'hull_bulwark', turretId: 'turret_stutter', hullName: 'Bulwark', turretName: 'Stutter' },
  { hullId: 'hull_vanguard', turretId: 'turret_carom', hullName: 'Vanguard', turretName: 'Carom' },
  { hullId: 'hull_juggernaut', turretId: 'turret_impaler', hullName: 'Juggernaut', turretName: 'Impaler' },
  { hullId: 'hull_whippet', turretId: 'turret_tempest', hullName: 'Whippet', turretName: 'Tempest' },
  { hullId: 'hull_warden', turretId: 'turret_bellows', hullName: 'Warden', turretName: 'Bellows' },
  { hullId: 'hull_paladin', turretId: 'turret_railspike', hullName: 'Paladin', turretName: 'Railspike' },
  { hullId: 'hull_phantom', turretId: 'turret_vespid', hullName: 'Phantom', turretName: 'Vespid' },
];

/**
 * A weight class off the pair's own tier — a marketing word for a real
 * number rather than a second, invented one. Tiers 1–2 read as one bracket:
 * the game's own tier cap groups them the same way (see the AI tier ceiling
 * table in `CLAUDE.game.md`), and a card is not the place to explain why a
 * Scout and a Dart are technically one tier apart.
 */
function weightClassForTier(tier: number): string {
  if (tier <= 2) return 'Light';
  if (tier === 3) return 'Medium';
  if (tier === 4) return 'Heavy';
  return 'Flagship';
}

/**
 * The one thing worth knowing about how this gun fights, off its own
 * mechanical shape (`WeaponProfile`) rather than a number nobody asked for.
 * Checked in an order that avoids repeating the archetype label right next
 * to it — a scatter turret's shell count is real but "Wide spread" says more
 * beside "Scattergun" than "8-round volley" would.
 */
function traitFor(turretId: string): string {
  const weapon = weaponFor(turretId);
  if (weapon.chargeTicks > 0) return 'Charges for more power';
  if (weapon.chainRadius > 0) return 'Arcs to a second target';
  if (weapon.pierce > 0) return 'Punches through armour';
  if (weapon.bounces > 0) return 'Ricochets off walls';
  if (weapon.blastRadius > 0) return 'Splash damage';
  if (weapon.scatterCone > 0.05) return 'Wide shotgun spread';
  if (weapon.shellsPerVolley > 1) return `${weapon.shellsPerVolley}-round volley`;
  if (weapon.armDistance > 0) return 'Arms at range';
  return 'True-flying rounds';
}

export interface ShowcaseCard extends ShowcaseSubject {
  paintId: string;
  /** The higher of the pair's two tiers — real, from `@shared/weapons`. */
  tier: number;
  /** `weightClassForTier(tier)` — a label on the same real number, not a second one. */
  weightClass: string;
  /** `ARCHETYPE_LABELS[weaponFor(turretId).archetype]` — real, not invented. */
  archetype: string;
  /** `traitFor(turretId)` — one real mechanical fact about the gun. */
  trait: string;
}

/** Every field but the two names is derived, never restated. */
export const SHOWCASE: readonly ShowcaseCard[] = SHOWCASE_SUBJECTS.map((subject) => {
  const tier = Math.max(tierForHull(subject.hullId), tierForTurret(subject.turretId));
  return {
    ...subject,
    paintId: STOCK_PAINT_ID,
    tier,
    weightClass: weightClassForTier(tier),
    archetype: ARCHETYPE_LABELS[weaponFor(subject.turretId).archetype],
    trait: traitFor(subject.turretId),
  };
});

// ─── Derived headline figures ────────────────────────────────────────────────

/**
 * Counts the garage catalogue holds and this page cannot import.
 *
 * `catalogue.data.ts` is server-side, so these are literals — and the *only*
 * literals on the page that describe the game's size. `landing-content.test.ts`
 * asserts the arithmetic between them (hulls × turrets = pairs), which is the
 * half a test can check; the counts themselves are checked by the backend's own
 * `catalogue-parity.test.ts` and by whoever adds the twenty-second hull.
 */
export const CATALOGUE = {
  hulls: 21,
  turrets: 25,
  /** Every hull/turret combination the fit matrix seats. */
  pairs: 21 * 25,
  /** Bought parts, across eight slots per hull. */
  parts: 32,
  partSlots: 8,
  researchNodes: 105,
  researchBranches: 5,
  researchTiers: 7,
  liveries: 10,
  titles: 10,
  /**
   * The bound premium items are held to, as a percentage.
   *
   * `PREMIUM_EDGE` in `economy/premium.data.ts` is 0.20, and it is measured
   * rather than chosen: across every tier and axis the credit roster's own best
   * item is a median of +20% ahead of the runner-up beside it. So this is the
   * most load-bearing number on the page — it is the answer to "is it
   * pay-to-win" — and it is the one worth checking by hand against that file.
   */
  premiumEdgePercent: 20,
} as const;

/** How many more pairs the shop has beyond the ten on show, for the last card. */
export const SHOWCASE_REMAINDER = CATALOGUE.pairs - SHOWCASE.length;

export interface Figure {
  /** The number, already formatted with its unit. */
  value: string;
  /** What it measures, in four words or fewer. */
  label: string;
}

/**
 * The hero's three figures.
 *
 * Chosen because each one answers a different objection about the game's
 * *size* rather than its netcode: how much is there to build (every hull
 * fitted to every gun), how much ground to fight on, and what a room actually
 * looks like. None of them is a vanity metric — `CATALOGUE.pairs` is
 * arithmetic on two real catalogue counts, `MAP_SUMMARIES.length` is the
 * picker's own list, and `TEAM_SIZE` is the constant a map's roster is dealt
 * against — so retuning any of the three moves this page with it. The
 * server-simulation and shell-velocity figures moved to the claims below
 * rather than off the page; see `CLAIMS`.
 */
export const HERO_FIGURES: readonly Figure[] = [
  { value: `${CATALOGUE.pairs}`, label: 'Tanks to build' },
  { value: `${MAP_SUMMARIES.length}`, label: 'Battlefields' },
  { value: `${TEAM_SIZE} vs ${TEAM_SIZE}`, label: 'A side' },
];

// ─── The thesis: hull and turret, as two decisions ───────────────────────────

export interface PartBlock {
  /** The small stylised label on the block — "The hull", "Turret". */
  heading: string;
  /** Which half `ItemPreview` draws. */
  framing: 'hull' | 'turret';
  /** `CATALOGUE.hulls` or `CATALOGUE.turrets` — real, not restated. */
  count: number;
  countLabel: string;
  body: string;
  /** Three plain words for what changing this part actually changes. */
  impacts: readonly string[];
}

/**
 * The two blocks the "One tank, two minds" section is built from.
 *
 * Deliberately not written as netcode claims any more — this is the page's
 * marketing thesis, not its technical one (that case is made elsewhere, in
 * the questions section). It is still exactly two things, though, which is
 * why the section keeps its heading: a hull decides what the tank *is*, a
 * turret decides what it *does*, and the two are chosen independently.
 *
 * `count` is real (`CATALOGUE.hulls` / `.turrets`); the rest is plain-language
 * copy rather than a mechanic pulled from the simulation, on purpose — a
 * visitor deciding whether to try the game does not need to know what a tick
 * rate is to understand that a light hull is fast and fragile.
 */
export const HULL_PART: PartBlock = {
  heading: 'The hull',
  framing: 'hull',
  count: CATALOGUE.hulls,
  countLabel: 'hulls to build from',
  body:
    'Everything under the ring — plate, tracks, engine. Light gets you in and out fast; heavy just stands there and takes the hit.',
  impacts: ['Speed', 'Armour', 'Handling'],
};

export const TURRET_PART: PartBlock = {
  heading: 'Turret',
  framing: 'turret',
  count: CATALOGUE.turrets,
  countLabel: 'guns to mount on it',
  body:
    'The gun on top, aimed wherever you’re looking — not wherever the hull points. Some hit fast and light, some hit once and end it.',
  impacts: ['Firepower', 'Reload', 'Range'],
};

// ─── Battlefields ────────────────────────────────────────────────────────────

export interface GroundPlate {
  id: string;
  name: string;
  blurb: string;
  /** `140 × 210 m`, from the map's own half-extent. */
  size: string;
  /** `4 a side`, from the map's own team size. */
  side: string;
  /** True when the map gives each team a fortified base to start from. */
  bases: boolean;
}

/**
 * The two battlefields the picker offers that are not really a fight: the
 * Proving Ground (`empty`) has no bases and no other team's crew, and the
 * Gunnery Range (`range`) is three tanks that never shoot back. Both are real
 * and pickable in the garage, but a gallery selling "ground that plays
 * differently" is selling battles, not test ranges — so they are left out
 * here rather than left in as two cards with nothing to say about a fight.
 */
const LANDING_EXCLUDED_MAP_IDS: readonly string[] = ['empty', 'range'];

/**
 * Every battlefield worth showing a visitor, off `MAP_SUMMARIES` — the
 * picker's own list, minus the two practice ranges above.
 *
 * Everything else is not a hand-written list: a map added to `@shared/maps`
 * appears here, with its own name, its own blurb and its own measured size,
 * and gets its picture from the same `/assets/maps/<id>.webp` convention the
 * picker uses.
 */
export const GROUND: readonly GroundPlate[] = MAP_SUMMARIES.filter(
  (m) => !LANDING_EXCLUDED_MAP_IDS.includes(m.id),
).map((m) => ({
  id: m.id,
  name: m.name,
  blurb: m.blurb,
  size: `${m.halfExtent.x * 2} × ${m.halfExtent.z * 2} m`,
  side: `${m.teamSize} a side`,
  bases: m.teamBases,
}));

// ─── Ranks ───────────────────────────────────────────────────────────────────

/**
 * How many rungs the ladder has.
 *
 * `MAX_RANK` in `backend/src/game/garage/economy.ts`, which is pure but lives in
 * the garage rather than in `@shared` — and `@shared` is the server's to widen,
 * so this page does not reach across for one integer. It is pinned instead
 * against something that *does* live here: `landing-content.test.ts` counts the
 * insignia in `public/assets/ranks/`, so a twenty-first rank's art landing in
 * the repo fails this number rather than quietly making the ladder short.
 *
 * The rung *names* are deliberately not copied. Twenty military ranks is a wall
 * of text nobody reads, the insignia are the part worth showing, and naming only
 * the two ends states the span without maintaining a table that has no test.
 */
export const RANK_COUNT = 20;

/** The two ends of the ladder, from `RANK_TITLES`. */
export const RANK_FIRST = 'Recruit';
export const RANK_LAST = 'Marshal';

/**
 * The deepest rank that gates anything in the shop.
 *
 * `SHOP_CEILING` in the backend's `stat-resolution.test.ts`. Above it the
 * ladder hands over titles instead of vehicles, which is the honest thing to
 * say about ranks 18–20 and the reason they exist.
 */
export const SHOP_CEILING_RANK = 17;

/** Insignia live at `/assets/ranks/rank-07.webp`. One line for a 21st rank. */
export function rankArt(rank: number): string {
  return `/assets/ranks/rank-${String(rank).padStart(2, '0')}.webp`;
}

// ─── Rank groups ─────────────────────────────────────────────────────────────

export interface RankGroup {
  id: string;
  name: string;
  from: number;
  to: number;
  /** The badge waiting at the top of the bracket — `rankArt(to)`. */
  art: string;
  mechanic: string;
  /**
   * Hull+turret combinations open by the top of this bracket — the same
   * arithmetic `CATALOGUE.pairs` is, at a tier ceiling short of the whole
   * roster instead of at it.
   */
  combos: number;
  /**
   * How this bracket's `combos` is worth saying out loud: the first bracket
   * states its absolute count ("70"), the last states it as the whole roster
   * ("All 525"), and everything between states the *increase* over the
   * bracket before it ("+367") — derived below, never a second hand-counted
   * figure. `unlockUnit` is the word or two after it.
   */
  unlockValue: string;
  unlockUnit: string;
}

/**
 * Every hull and turret at or under `maxTier`, multiplied out. Derived from
 * `HULL_PROFILES`/`TURRET_PROFILES` — the same tables the AI tier ceiling
 * itself reads — rather than a second, hand-counted table that could drift
 * from the roster the moment a hull is added.
 */
function combosUpToTier(maxTier: number): number {
  const hulls = Object.values(HULL_PROFILES).filter((h) => h.tier <= maxTier).length;
  const turrets = Object.values(TURRET_PROFILES).filter((t) => t.tier <= maxTier).length;
  return hulls * turrets;
}

/**
 * The twenty-rung ladder, grouped in three for a summary a visitor can read
 * in one pass — the full ladder is still further down the page (`#ranks`),
 * insignia and all.
 *
 * The two breakpoints (rank 8, rank 15) are not a rough third each: they are
 * where the AI's own tier ceiling actually steps, in `CLAUDE.game.md`'s tier
 * cap table (an AI tank may carry tier ≤2 through rank 7, ≤4 through rank 14,
 * and everything from rank 15 on — the real table moves in five steps, at
 * ranks 1/3/8/12/15; three groups is this page's simplification of it, not a
 * second, invented one). `combos` is computed at each group's own ceiling —
 * 2, 4 and 5 — so the figure a visitor reads is the same one the AI's own
 * cap would let them meet.
 */
/** `RANK_GROUPS`, before each entry gets its derived `unlockValue`/`unlockUnit`. */
const RANK_GROUPS_BASE: readonly Omit<RankGroup, 'unlockValue' | 'unlockUnit'>[] = [
  {
    id: 'starting',
    name: 'Starting out',
    from: 1,
    to: 7,
    art: rankArt(7),
    mechanic:
      'Every AI you meet in this bracket is still driving something you could already afford yourself. The fight teaches you the controls — it does not test them.',
    combos: combosUpToTier(2),
  },
  {
    id: 'proving',
    name: 'Proving ground',
    from: 8,
    to: 14,
    art: rankArt(14),
    mechanic:
      'The opposition gets serious. Tougher AI crews turn up driving heavier iron — and by now, so can you.',
    combos: combosUpToTier(4),
  },
  {
    id: 'veteran',
    name: 'Open field',
    from: 15,
    to: RANK_COUNT,
    art: rankArt(RANK_COUNT),
    mechanic: `Nothing is held back any more — every hull, every gun. Rank ${SHOP_CEILING_RANK} is the deepest gate in the whole shop, and the last three ranks pay out in reputation instead of another tank.`,
    combos: combosUpToTier(5),
  },
];

/**
 * The first bracket states what is open outright; the last states it as the
 * whole roster; anything in between states the *increase* over the bracket
 * before it, because "70 builds" beside "437 builds" reads as unrelated
 * figures where "+367 unlocked" reads as the ladder actually climbing.
 */
export const RANK_GROUPS: readonly RankGroup[] = RANK_GROUPS_BASE.map((group, i) => {
  const isFirst = i === 0;
  const isLast = i === RANK_GROUPS_BASE.length - 1;
  return {
    ...group,
    unlockValue: isFirst
      ? `${group.combos}`
      : isLast
        ? `All ${group.combos}`
        : `+${group.combos - RANK_GROUPS_BASE[i - 1].combos}`,
    unlockUnit: isFirst || isLast ? 'builds available' : 'builds unlocked',
  };
});

// ─── Questions ───────────────────────────────────────────────────────────────

// ─── Research ────────────────────────────────────────────────────────────────

export interface ResearchExample {
  id: string;
  name: string;
  blurb: string;
  cost: number;
  branch: string;
  tier: number;
  icon: string;
}

/**
 * `res_running_gear` is a real node from `garage/catalogue.data.ts` — mobility
 * branch, tier 1, no prerequisites, 320 credits — copied here rather than
 * imported because the research catalogue lives server-side and is not
 * `@shared`. It is the tree's own root, and it is the example for exactly that
 * reason: it is the node a brand-new account can actually afford to start.
 */
export const RESEARCH_EXAMPLE: ResearchExample = {
  id: 'res_running_gear',
  name: 'Running Gear',
  blurb:
    'A bench, a hoist and somebody who has taken a final drive apart before. Where every fast tank starts.',
  cost: 320,
  branch: 'Mobility',
  tier: 1,
  icon: '/assets/research/res-running-gear.webp',
};

export interface ResearchFact {
  stat: string;
  label: string;
}

export const RESEARCH_FACTS: readonly ResearchFact[] = [
  { stat: `${CATALOGUE.researchNodes}`, label: 'nodes' },
  { stat: `${CATALOGUE.researchBranches}`, label: 'branches' },
  { stat: `${CATALOGUE.researchTiers}`, label: 'rows deep' },
];

export interface Question {
  q: string;
  a: string;
}

/**
 * Six questions, written for a first-time visitor rather than for a developer:
 * plain, benefit-led answers with no internal mechanics and no figures that
 * would need updating every time a balance number moves. Order is deliberate —
 * remove the cost objection, remove the friction objection, address the
 * fairness question, reassure there is always a game to join, then the two
 * device-capability questions.
 */
export const QUESTIONS: readonly Question[] = [
  {
    q: 'Is Steel of War free to play?',
    a: 'Yes. Play as much as you want without paying a cent — every tank and weapon can be earned by battling. A few optional cosmetic and convenience purchases are there if you want to move faster, never to win.',
  },
  {
    q: 'Do I need to download or install anything?',
    a: 'No. Steel of War runs straight in your browser. Create an account and you’re in the garage in seconds — no installers, no patches, no waiting.',
  },
  {
    q: 'Is it pay-to-win?',
    a: 'No. Every hull and weapon in the game can be unlocked simply by playing. Spending money buys convenience and style, not an advantage over other players.',
  },
  {
    q: 'Will I always have someone to play against?',
    a: 'Always. Jump into a match any time and you’ll find a full battlefield waiting, opponents included. You’ll never be stuck looking for a fight.',
  },
  {
    q: 'Can I play on my phone?',
    a: 'Yes — Steel of War is playable on mobile browsers, with a dedicated touch interface built for it. Sign in from your phone and drop straight into the garage.',
  },
  {
    q: 'Will it run well on my computer?',
    a: 'Steel of War is built to run smoothly across a wide range of hardware, with graphics settings you can adjust to fit your setup.',
  },
];

// ─── Navigation ──────────────────────────────────────────────────────────────

export interface NavItem {
  href: string;
  label: string;
}

/**
 * Every one of these is an in-page anchor, and `LandingScreen.vue`'s `goTo()`
 * assumes it: `querySelector(href)` on a `/`-rooted path is not a valid
 * selector. News is deliberately **not** in this list — it is a real route
 * (`state/news-route.ts`) rather than a section of this page, and it is
 * linked separately, through `navigateNews()`, everywhere `NAV` is drawn.
 */
export const NAV: readonly NavItem[] = [
  { href: '#showcase', label: 'Tanks' },
  { href: '#progression', label: 'Progression' },
  { href: '#thesis', label: 'The idea' },
  { href: '#ground', label: 'Ground' },
  { href: '#research', label: 'Research' },
  { href: '#questions', label: 'FAQ' },
];
